import propTypes from 'prop-types';
import { Inline, Stack, Text, textVariants } from '@etg/wings';
import { productIds } from '@eti/products';
import { css, etiColors } from '@eti/styles';
import { CartRow } from '../../cart/components/CartComponents';
import CartToggleableRow from '../../cart/components/CartToggleableRow';

const numberOfUnitsFor = {
  BuySpecificationCartTravelerBaggage: ({ travelerBaggageTripPrices }) =>
    travelerBaggageTripPrices.filter(
      (el, i, arr) => arr.findIndex((obj) => obj.travelerId === el.travelerId) === i,
    ).length,
  BuySpecificationCartTraveler: ({ travelerPrices }) => travelerPrices.length,
  BuySpecificationCartTravelerChoice: ({ travelerChoicePrices }) => travelerChoicePrices.length,
  BuySpecificationCartTravelerSeatMap: () => '',
  BuySpecificationCartTravelerCabinBaggage: ({ travelerCabinBaggagePrices }) =>
    travelerCabinBaggagePrices ? travelerCabinBaggagePrices.length : 0,
  [null]: () => 1,
};

const groupProductsByName = (array) => {
  return array.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.name]: (acc[curr.name] || 0) + curr.numberOfUnits,
    }),
    {},
  );
};

const availabilityStyles = css`
  justify-content: space-between;
`;

const contentStyles = css`
  color: ${etiColors.grayScale[10]};
  padding-inline-start: 8px;
`;

const getExtraProductsItems = (products) => {
  const extraProducts = products.map(({ buySpecification, id, title, availability, name }) => {
    const specification = buySpecification ? buySpecification.__typename : null;
    const numberOfUnits = numberOfUnitsFor[specification](buySpecification) || 0;
    return { name: title || name, numberOfUnits, id, availability };
  });
  const mergedExtraProducts = groupProductsByName(extraProducts);
  return Object.keys(mergedExtraProducts).map((name, index) => {
    const numberOfUnits = mergedExtraProducts[name];
    const productId = extraProducts[index].id;
    return {
      label: numberOfUnits ? `${numberOfUnits} ${name}` : name,
      id: productId,
      name,
      availability: extraProducts[index].availability ?? null,
    };
  });
};

const ExtraProducts = ({ products = [], addedAncillaryBundles = [] }) => {
  const extraProducts = getExtraProductsItems(products);
  return (
    <Stack data-testid="postBooking-extra-products" role="list" spacing={4}>
      {extraProducts.map(({ id, label, availability, name }) => {
        if (availability && availability.hasAvailability) {
          return (
            <Stack
              data-testid={`cart-product-${productIds[Number(id)]}`}
              key={id}
              role="listitem"
              spacing={0}
            >
              <Text>{name}</Text>
              {availability?.airportList.map(({ airportName, numberOfPass }) => (
                <Inline className={availabilityStyles} key={airportName}>
                  <Text className={contentStyles} variant={textVariants._12}>
                    {airportName}
                  </Text>
                  <Text
                    className={contentStyles}
                    variant={textVariants._12}
                  >{`${numberOfPass} ${availability.passLabel}`}</Text>
                </Inline>
              ))}
            </Stack>
          );
        }

        return (
          <CartRow data-testid={`cart-product-${productIds[Number(id)]}`} key={id} role="listitem">
            <Text>{label}</Text>
          </CartRow>
        );
      })}
      {addedAncillaryBundles?.length > 0 &&
        addedAncillaryBundles.map((addedAncillaryBundle) => (
          <CartToggleableRow
            data={addedAncillaryBundle.content}
            dataTestId={`cart-product-${productIds[Number(addedAncillaryBundle.id)]}`}
            key={addedAncillaryBundle.name}
            title={addedAncillaryBundle.name}
          />
        ))}
    </Stack>
  );
};

ExtraProducts.propTypes = {
  addedAncillaryBundles: propTypes.arrayOf(propTypes.object),
  products: propTypes.arrayOf(propTypes.object),
};

export default ExtraProducts;
